import dayjs from 'dayjs'
import { getMPDataWithParams } from 'src/mentor/services'

export const EMPTY_ROSTER_ASSIGNMENT_INDEX = {
  byRoomId: {},
  byLocationKey: {},
  byBrandId: {},
  orderedRoomGroups: [],
  summary: {
    totalAssignments: 0,
    totalRooms: 0,
    currentCount: 0,
    upcomingCount: 0,
    primaryGroup: null,
  },
}

export function buildBrandsFromLocations(locations = []) {
  return [
    ...new Map(
      locations?.map((item) => [
        item?.clinicBrandId,
        {
          id: item?.clinicBrandId,
          title: item?.clinicBrandName,
          ownerUserIds: item?.clinicBrandOwnerUserIds,
          icon: item?.clinicBrandIconImage || item?.clinicBrandIcon || item?.icon,
          coverIcon: item?.clinicBrandCoverIcon || item?.coverIcon,
          coverImage: item?.clinicBrandCoverImage || item?.clinicBrandImage || item?.coverImage,
          locations: locations?.filter((loc) => loc?.clinicBrandId === item?.clinicBrandId),
        },
      ]),
    ).values(),
  ]
}

export function buildRosterRoomTargetsFromBrands(brands = []) {
  return brands.flatMap((brand) =>
    (brand?.locations || []).flatMap((location) =>
      (location?.rooms || [])
        .filter((room) => Boolean(room?.id))
        .map((room) => ({
          roomId: room?.id,
          chamberRoomTitle: room?.title || room?.name || 'Room unit',
          clinicBrandId: brand?.id || location?.clinicBrandId,
          clinicBrandName: brand?.title || location?.clinicBrandName || 'Clinic brand',
          clinicLocationId: location?.id,
          clinicLocationName: location?.name || location?.clinicLocationName || 'Clinic location',
        })),
    ),
  )
}

export function getRosterAssignmentsFromResponse(response) {
  const responseData =
    response?.data?.data ??
    response?.data?.payload ??
    response?.data ??
    response?.payload ??
    response ??
    []

  const rosterSlots =
    responseData?.rosterSlots ||
    responseData?.['clinic-roster-slots'] ||
    responseData?.['clinic-roster-slot'] ||
    responseData?.slots ||
    responseData

  if (Array.isArray(rosterSlots)) {
    return rosterSlots
  }

  return rosterSlots ? [rosterSlots] : []
}

export function getRosterAssignmentDateTime(assignment, type = 'start') {
  const rosterDate = assignment?.slotDate || assignment?.customDate || assignment?.date
  const rosterTime =
    type === 'end'
      ? assignment?.slotEndTime || assignment?.endTime
      : assignment?.slotStartTime || assignment?.startTime

  if (!rosterDate || !rosterTime) {
    return null
  }

  return dayjs(`${rosterDate}T${rosterTime}`)
}

export function compareRosterAssignments(leftAssignment, rightAssignment) {
  const leftStartTime = getRosterAssignmentDateTime(leftAssignment)
  const rightStartTime = getRosterAssignmentDateTime(rightAssignment)

  if (!leftStartTime && !rightStartTime) return 0
  if (!leftStartTime) return 1
  if (!rightStartTime) return -1

  return leftStartTime.diff(rightStartTime)
}

export function formatRosterAssignmentTimeLabel(assignment) {
  const startTime = getRosterAssignmentDateTime(assignment, 'start')
  const endTime = getRosterAssignmentDateTime(assignment, 'end')

  if (startTime && endTime) {
    return `${startTime.format('h:mm A')} - ${endTime.format('h:mm A')}`
  }

  if (startTime) {
    return startTime.format('h:mm A')
  }

  return 'Time TBD'
}

export function getRosterAssignmentStatus(assignment, now = dayjs()) {
  const startTime = getRosterAssignmentDateTime(assignment, 'start')
  const endTime = getRosterAssignmentDateTime(assignment, 'end')

  if (
    startTime &&
    endTime &&
    (now.isAfter(startTime) || now.isSame(startTime)) &&
    now.isBefore(endTime)
  ) {
    return 'current'
  }

  if (startTime && now.isBefore(startTime)) {
    return 'upcoming'
  }

  return 'completed'
}

export function buildRosterAssignmentGroup(assignments = [], now = dayjs()) {
  const sortedAssignments = [...assignments].sort(compareRosterAssignments)
  const currentAssignment = sortedAssignments.find(
    (assignment) => getRosterAssignmentStatus(assignment, now) === 'current',
  )
  const upcomingAssignment = sortedAssignments.find(
    (assignment) => getRosterAssignmentStatus(assignment, now) === 'upcoming',
  )
  const primaryAssignment = currentAssignment || upcomingAssignment || sortedAssignments?.[0] || null
  const status = currentAssignment ? 'current' : upcomingAssignment ? 'upcoming' : 'completed'
  const assignmentCount = sortedAssignments.length
  const timeLabel = primaryAssignment ? formatRosterAssignmentTimeLabel(primaryAssignment) : 'Time TBD'

  return {
    assignments: sortedAssignments,
    assignmentCount,
    currentAssignment,
    primaryAssignment,
    status,
    statusLabel:
      status === 'current'
        ? 'Roster now'
        : status === 'upcoming'
        ? 'Rostered next'
        : 'Rostered today',
    timeLabel,
    detailLabel: assignmentCount > 1 ? `${timeLabel} · ${assignmentCount} slots today` : timeLabel,
    clinicBrandId: primaryAssignment?.clinicBrandId || null,
    clinicBrandName: primaryAssignment?.clinicBrandName || 'Clinic brand',
    clinicLocationId: primaryAssignment?.clinicLocationId || null,
    clinicLocationName: primaryAssignment?.clinicLocationName || 'Clinic location',
    chamberRoomId: primaryAssignment?.chamberRoomId || null,
    chamberRoomTitle: primaryAssignment?.chamberRoomTitle || 'Room unit',
  }
}

export function buildRosterAssignmentIndex(assignments = [], now = dayjs()) {
  const assignmentsByRoomId = {}
  const assignmentsByLocationKey = {}
  const assignmentsByBrandId = {}

  assignments.forEach((assignment) => {
    const roomId = assignment?.chamberRoomId
    const brandId = assignment?.clinicBrandId
    const locationKey =
      assignment?.clinicBrandId && assignment?.clinicLocationId
        ? `${assignment.clinicBrandId}-${assignment.clinicLocationId}`
        : null

    if (roomId) {
      assignmentsByRoomId[roomId] = [...(assignmentsByRoomId[roomId] || []), assignment]
    }

    if (locationKey) {
      assignmentsByLocationKey[locationKey] = [
        ...(assignmentsByLocationKey[locationKey] || []),
        assignment,
      ]
    }

    if (brandId) {
      assignmentsByBrandId[brandId] = [...(assignmentsByBrandId[brandId] || []), assignment]
    }
  })

  const byRoomId = Object.fromEntries(
    Object.entries(assignmentsByRoomId).map(([roomId, roomAssignments]) => [
      roomId,
      buildRosterAssignmentGroup(roomAssignments, now),
    ]),
  )
  const byLocationKey = Object.fromEntries(
    Object.entries(assignmentsByLocationKey).map(([locationKey, locationAssignments]) => [
      locationKey,
      buildRosterAssignmentGroup(locationAssignments, now),
    ]),
  )
  const byBrandId = Object.fromEntries(
    Object.entries(assignmentsByBrandId).map(([brandId, brandAssignments]) => [
      brandId,
      buildRosterAssignmentGroup(brandAssignments, now),
    ]),
  )

  const statusRank = {
    current: 0,
    upcoming: 1,
    completed: 2,
  }

  const orderedRoomGroups = Object.values(byRoomId).sort((leftGroup, rightGroup) => {
    const rankDifference =
      (statusRank[leftGroup?.status] ?? 99) - (statusRank[rightGroup?.status] ?? 99)
    if (rankDifference !== 0) {
      return rankDifference
    }

    return compareRosterAssignments(leftGroup?.primaryAssignment, rightGroup?.primaryAssignment)
  })

  return {
    byRoomId,
    byLocationKey,
    byBrandId,
    orderedRoomGroups,
    summary: {
      totalAssignments: assignments.length,
      totalRooms: orderedRoomGroups.length,
      currentCount: orderedRoomGroups.filter((group) => group?.status === 'current').length,
      upcomingCount: orderedRoomGroups.filter((group) => group?.status === 'upcoming').length,
      primaryGroup: orderedRoomGroups?.[0] || null,
    },
  }
}

export function isCheckedIntoRosterRoom(checkedInDetails, assignmentOrGroup) {
  if (!checkedInDetails || !assignmentOrGroup) {
    return false
  }

  const chamberRoomId = assignmentOrGroup?.chamberRoomId || assignmentOrGroup?.primaryAssignment?.chamberRoomId
  const clinicLocationId =
    assignmentOrGroup?.clinicLocationId || assignmentOrGroup?.primaryAssignment?.clinicLocationId
  const clinicBrandId =
    assignmentOrGroup?.clinicBrandId || assignmentOrGroup?.primaryAssignment?.clinicBrandId

  return (
    String(checkedInDetails?.chamberRoomId || '') === String(chamberRoomId || '') &&
    String(checkedInDetails?.clinicLocationId || '') === String(clinicLocationId || '') &&
    String(checkedInDetails?.clinicBrandId || '') === String(clinicBrandId || '')
  )
}

const ROSTER_SESSION_START_EVENT_IDS = new Set(['log-check-in', 'log-check-in-refresh'])
const ROSTER_SESSION_END_EVENT_IDS = new Set(['log-check-out', 'log-thrown-out'])

function isRosterSessionStartLog(logEventId) {
  return ROSTER_SESSION_START_EVENT_IDS.has(logEventId)
}

function isRosterSessionEndLog(logEventId) {
  return ROSTER_SESSION_END_EVENT_IDS.has(logEventId)
}

function doRosterIntervalsOverlap(intervalStart, intervalEnd, assignmentStart, assignmentEnd) {
  if (!intervalStart || !assignmentStart) {
    return false
  }

  const safeIntervalEnd = intervalEnd || intervalStart
  const safeAssignmentEnd = assignmentEnd || assignmentStart

  return (
    (safeIntervalEnd.isAfter(assignmentStart) || safeIntervalEnd.isSame(assignmentStart)) &&
    (intervalStart.isBefore(safeAssignmentEnd) || intervalStart.isSame(safeAssignmentEnd))
  )
}

export function buildRosterPresenceIntervalsByRoomId(usageLogs = [], now = dayjs()) {
  const logsByRoomId = usageLogs.reduce((accumulator, usageLog) => {
    const roomId = usageLog?.chamberRoomId
    const loggedAt = usageLog?.loggedAt ? dayjs(usageLog.loggedAt) : null

    if (!roomId || !loggedAt?.isValid?.()) {
      return accumulator
    }

    accumulator[roomId] = [...(accumulator[roomId] || []), { ...usageLog, __loggedAt: loggedAt }]
    return accumulator
  }, {})

  return Object.fromEntries(
    Object.entries(logsByRoomId).map(([roomId, roomLogs]) => {
      const sortedLogs = [...roomLogs].sort((leftLog, rightLog) =>
        leftLog.__loggedAt.diff(rightLog.__loggedAt),
      )
      const intervals = []
      let activeSessionStart = null

      sortedLogs.forEach((usageLog) => {
        if (isRosterSessionStartLog(usageLog?.logEventId)) {
          if (!activeSessionStart) {
            activeSessionStart = usageLog.__loggedAt
          }
          return
        }

        if (isRosterSessionEndLog(usageLog?.logEventId) && activeSessionStart) {
          intervals.push({
            start: activeSessionStart,
            end: usageLog.__loggedAt,
          })
          activeSessionStart = null
        }
      })

      if (activeSessionStart) {
        intervals.push({
          start: activeSessionStart,
          end: now,
        })
      }

      return [roomId, intervals]
    }),
  )
}

export function getRosterJoinedStatusForAssignmentGroup(
  assignmentGroup,
  presenceIntervalsByRoomId = {},
  now = dayjs(),
) {
  const chamberRoomId =
    assignmentGroup?.chamberRoomId || assignmentGroup?.primaryAssignment?.chamberRoomId
  const roomPresenceIntervals = presenceIntervalsByRoomId?.[chamberRoomId] || []
  const assignments = assignmentGroup?.assignments || []
  const assignmentsWithTime = assignments.filter((assignment) =>
    Boolean(getRosterAssignmentDateTime(assignment, 'start')),
  )
  const targetAssignments = assignmentsWithTime.length > 0 ? assignmentsWithTime : assignments

  const joinedAssignmentCount = targetAssignments.filter((assignment) => {
    const assignmentStart = getRosterAssignmentDateTime(assignment, 'start')
    const assignmentEnd = getRosterAssignmentDateTime(assignment, 'end')

    if (!assignmentStart) {
      return false
    }

    return roomPresenceIntervals.some((presenceInterval) =>
      doRosterIntervalsOverlap(
        presenceInterval?.start,
        presenceInterval?.end || now,
        assignmentStart,
        assignmentEnd,
      ),
    )
  }).length

  const totalAssignments = targetAssignments.length
  const status =
    joinedAssignmentCount === 0
      ? 'missed'
      : joinedAssignmentCount >= totalAssignments
      ? 'joined'
      : 'partial'

  return {
    status,
    joinedAssignmentCount,
    totalAssignments,
    joined: joinedAssignmentCount > 0,
  }
}

export function getRosterReminderCandidate({
  assignmentIndex = EMPTY_ROSTER_ASSIGNMENT_INDEX,
  checkedInDetails = null,
  now = dayjs(),
  upcomingLeadMinutes = 15,
  overdueLeadMinutes = 10,
}) {
  const roomGroups = assignmentIndex?.orderedRoomGroups || []

  const currentPendingGroup = roomGroups.find((group) => {
    if (group?.status !== 'current') return false
    return !isCheckedIntoRosterRoom(checkedInDetails, group)
  })

  if (currentPendingGroup) {
    const startTime = getRosterAssignmentDateTime(currentPendingGroup?.primaryAssignment, 'start')
    const minutesSinceStart = startTime ? now.diff(startTime, 'minute') : null

    return {
      type:
        minutesSinceStart !== null && minutesSinceStart >= overdueLeadMinutes
          ? 'overdue'
          : 'current',
      group: currentPendingGroup,
      startTime,
      minutesSinceStart,
    }
  }

  const upcomingPendingGroup = roomGroups.find((group) => {
    if (group?.status !== 'upcoming') return false
    if (isCheckedIntoRosterRoom(checkedInDetails, group)) return false

    const startTime = getRosterAssignmentDateTime(group?.primaryAssignment, 'start')
    if (!startTime) return false

    const minutesUntilStart = startTime.diff(now, 'minute')
    return minutesUntilStart >= 0 && minutesUntilStart <= upcomingLeadMinutes
  })

  if (!upcomingPendingGroup) {
    return null
  }

  const startTime = getRosterAssignmentDateTime(upcomingPendingGroup?.primaryAssignment, 'start')

  return {
    type: 'upcoming',
    group: upcomingPendingGroup,
    startTime,
    minutesUntilStart: startTime ? startTime.diff(now, 'minute') : null,
  }
}

export async function fetchMentorRosterAssignments({
  mentorId,
  roomTargets = [],
  rosterDate = dayjs().format('YYYY-MM-DD'),
  fetcher = getMPDataWithParams,
}) {
  if (!mentorId || roomTargets.length === 0) {
    return []
  }

  const rosterResponses = await Promise.allSettled(
    roomTargets.map(async (roomTarget) => {
      const response = await fetcher('/api/secure/teacher/clinic-roster', {
        fromDate: rosterDate,
        toDate: rosterDate,
        roomId: roomTarget.roomId,
      })

      return {
        roomTarget,
        response,
      }
    }),
  )

  return rosterResponses
    .flatMap((result) => {
      if (result.status !== 'fulfilled') {
        return []
      }

      const { roomTarget, response } = result.value

      if (response?.status !== 200) {
        return []
      }

      return getRosterAssignmentsFromResponse(response)
        .filter((rosterSlot) => String(rosterSlot?.mentorId) === String(mentorId))
        .map((rosterSlot) => ({
          id:
            rosterSlot?.id ||
            `${roomTarget.roomId}-${rosterSlot?.customDate || rosterDate}-${
              rosterSlot?.slotStartTime || rosterSlot?.startTime || 'start'
            }`,
          mentorId: rosterSlot?.mentorId || mentorId,
          slotDate: rosterSlot?.customDate || rosterSlot?.slotDate || rosterDate,
          slotStartTime: rosterSlot?.slotStartTime || rosterSlot?.startTime || null,
          slotEndTime: rosterSlot?.slotEndTime || rosterSlot?.endTime || null,
          clinicBrandId: rosterSlot?.clinicBrandId || roomTarget?.clinicBrandId,
          clinicBrandName: rosterSlot?.clinicBrandName || roomTarget?.clinicBrandName,
          clinicLocationId: rosterSlot?.clinicLocationId || roomTarget?.clinicLocationId,
          clinicLocationName: rosterSlot?.clinicLocationName || roomTarget?.clinicLocationName,
          chamberRoomId: rosterSlot?.chamberRoomId || roomTarget?.roomId,
          chamberRoomTitle: rosterSlot?.chamberRoomTitle || roomTarget?.chamberRoomTitle,
          rawRosterSlot: rosterSlot,
        }))
    })
    .sort(compareRosterAssignments)
}
